import { defineStore } from 'pinia';

import { useLoginUserStore } from '../login-user';

import {
  AutocompleteRepository,
  ShopResponse,
} from '@/repositories/generated';

type ShopAutocompleteRequestForm = {
  term: string | undefined;
  companyId: string | undefined;
};

type ShopAutocompleteStoreState = {
  searchCache: Record<string, ShopResponse>;
};

/**
 * 検索候補表示コンポーネント用にレスポンスをキャッシュするストア
 */
export const useShopAutocompleteStore = defineStore('shopAutocomplete', {
  state: (): ShopAutocompleteStoreState => ({
    searchCache: {},
  }),
  getters: {
    // idを元にキャッシュ、ログイン情報から取得するcomputed。なければ自動補完APIを非同期で叩く
    getByIdOrDummy: (state) => (id: string): ShopResponse => {
      const {
        isShopAutocompleteDisabled,
        loginShop,
      } = useLoginUserStore().autocompleteModel;

      if (loginShop?.id === id) {
        return loginShop;
      }

      const cache = state.searchCache[id];
      if (cache) {
        return cache;
      }

      // 自動補完APIが１度だけ叩かれるように、キャッシュにダミーデータを差し込んでおく
      const dummyItem = { id, name: '' } as ShopResponse;
      state.searchCache[id] = dummyItem;
      // キャッシュがないかつ権限がある場合は、自動補完APIを叩く
      if (!isShopAutocompleteDisabled) {
        (async () => {
          const res = await AutocompleteRepository.searchShop({ term: id, companyId: undefined });
          res.forEach((it) => {
            state.searchCache[it.id] = it;
          });
        })();
      }
      return dummyItem;
    },
    // nameを元にキャッシュ、ログイン情報から取得するcomputed。なければ自動補完APIを非同期で叩く
    getByNameOrDummy: (state) => (name: string): ShopResponse => {
      const {
        isShopAutocompleteDisabled,
        loginShop,
      } = useLoginUserStore().autocompleteModel;

      if (loginShop?.name === name) {
        return loginShop;
      }

      const cache = Object.values(state.searchCache).find((it) => it.name === name);
      if (cache) {
        return cache;
      }

      // 自動補完APIが１度だけ叩かれるように、キャッシュにダミーデータを差し込んでおく
      const dummyItem = { id: '', name } as ShopResponse;
      // name以外に情報がないので、若干dirtyだがnameをキーにしてキャッシュする
      state.searchCache[name] = dummyItem;
      // キャッシュがないかつ権限がある場合は、自動補完APIを叩く
      if (!isShopAutocompleteDisabled) {
        (async () => {
          const res = await AutocompleteRepository.searchShop({ term: name, companyId: undefined });
          res.forEach((it) => {
            state.searchCache[it.id] = it;
          });

          const cacheOfRealData = Object.values(state.searchCache).find((it) => it.name === name && it.id !== '');
          if (cacheOfRealData) {
            // nameをキーにしてキャッシュしたのを削除する
            delete state.searchCache[name];
          }
        })();
      }
      return dummyItem;
    },
  },
  actions: {
    async search(form: ShopAutocompleteRequestForm): Promise<ShopResponse[]> {
      const res = await AutocompleteRepository.searchShop(form);
      res.forEach((it) => {
        this.searchCache[it.id] = it;
      });
      return res;
    },
  },
});
